import {
  PersonalDetail,
  PersonalDetailInvalidKeys,
  PersonalDetailState,
  ProposalResponse,
  SinglePersonalDetailState,
} from '@/types/Proposal';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import format from 'date-fns/format';

const initialState: PersonalDetailState = {
  adultCount: 1,
  adultDetails: [],
  childDetails: [],
  seniorDetails: [],
  invalidKeys: {
    adult: [],
    child: [],
    senior: [],
  },
  loading: false,
  error: false,
};

export const personalDetailSlice = createSlice({
  name: 'personalDetail',
  initialState: initialState,
  reducers: {
    setAdultCount: (state, action: PayloadAction<number>) => {
      state.adultCount = action.payload;
    },
    setPersonalDetail: (
      state,
      action: PayloadAction<SinglePersonalDetailState>,
    ) => {
      if (action.payload.personalDetail.identityNumber?.trim() == '') {
        action.payload.personalDetail.identityNumber = '';
      }

      switch (action.payload.proposerType) {
        case 'adult':
          state.adultDetails[action.payload.proposerIndex] =
            action.payload.personalDetail;
          break;
        case 'child':
          state.childDetails[action.payload.proposerIndex] =
            action.payload.personalDetail;
          break;
        case 'senior':
          state.seniorDetails[action.payload.proposerIndex] =
            action.payload.personalDetail;
          break;
      }

      const policyHolderMailingAddress = state.adultDetails[0]?.mailingAddress;
      state.adultDetails.forEach((x) => {
        x.mailingAddress = x?.isSameMailingAddress
          ? policyHolderMailingAddress
          : x.mailingAddress;
      });
      state.childDetails.forEach((x) => {
        x.mailingAddress = x?.isSameMailingAddress
          ? policyHolderMailingAddress
          : x.mailingAddress;
      });
      state.seniorDetails.forEach((x) => {
        x.mailingAddress = x?.isSameMailingAddress
          ? policyHolderMailingAddress
          : x.mailingAddress;
      });
    },
    setInvalidKeys: (
      state,
      action: PayloadAction<PersonalDetailInvalidKeys>,
    ) => {
      state.invalidKeys = action.payload;
    },
    removeInvalidKey: (
      state,
      action: PayloadAction<{ type: string; removedKey: number }>,
    ) => {
      if (action.payload.type == 'adult') {
        if (state.invalidKeys?.adult)
          state.invalidKeys.adult = state.invalidKeys?.adult.filter(
            (x) => x !== action.payload.removedKey,
          );
      } else if (action.payload.type == 'child') {
        if (state.invalidKeys?.child)
          state.invalidKeys.child = state.invalidKeys?.child.filter(
            (x) => x !== action.payload.removedKey,
          );
      }
    },
    removeAllInvalidKeys: (state) => {
      state.invalidKeys = {
        adult: [],
        child: [],
        senior: [],
      };
    },
    mapProposalDataToPersonalDetail: (
      state,
      action: PayloadAction<ProposalResponse>,
    ) => {
      const data = action.payload;

      // Policy owner.
      state.adultDetails[0] = {
        fullName: data.policyOwner.name,
        identityType: data.policyOwner.identityType,
        identityNumber: data.policyOwner.identityNumber,
        nationality: data.policyOwner.nationality,
        dualNationality: data.policyOwner.dualNationality,
        secondaryIdentityType: data.policyOwner.secondaryIdentityType,
        secondaryIdentityNumber: data.policyOwner.secondaryIdentityNumber,        
        dob: format(
          typeof data.policyOwner.birthDate == 'string'
            ? new Date(data.policyOwner.birthDate)
            : data.policyOwner.birthDate,
          'dd/MM/yyyy',
        ),
        gender: data.policyOwner.gender,
        ethnicity: data.policyOwner.ethnicity.toString(),
        email: data.policyOwner.email,
        mobileNumber: data.policyOwner.mobile,
        isSameMailingAddress: false,
        mailingAddress: {
          buildingName: data.policyOwner.address?.addressLine1 ?? '',
          areaName: data.policyOwner.address?.addressLine2 ?? '',
          cityName: data.policyOwner.address?.city,
          postcode: data.policyOwner.address?.postcode ?? '',
          state: data.policyOwner.address?.state ?? '',
        },
        bankId: data.bankId,
        accountHolder: data.accountHolder,
        accountNo: data.accountNo,
      };

      // Insured.
      data.insured.forEach((x) => {
        const insured = {
          fullName: x.name,
          identityNumber: x.identityNumber,
          identityType: x.identityType,
          dualNationality: x.dualNationality,
          secondaryIdentityType: x.secondaryIdentityType,
          secondaryIdentityNumber: x.secondaryIdentityNumber,      
          nationality: x.nationality,
          dob: format(
            typeof x.birthDate == 'string'
              ? new Date(x.birthDate)
              : x.birthDate,
            'dd/MM/yyyy',
          ),
          gender: x.gender,
          ethnicity: x.ethnicity.toString(),
          email: x.email,
          mobileNumber: x.mobile,
          isSameMailingAddress: false,
          mailingAddress: {
            buildingName: x.address?.addressLine1 ?? '',
            areaName: x.address?.addressLine2 ?? '',
            cityName: x.address?.city,
            postcode: x.address?.postcode ?? '',
            state: x.address?.state ?? '',
          },
          ...x,
        };
        if (x.isChild) {
          state.childDetails.push(insured);
        } else {
          state.adultDetails.push(insured);
        }
      });
    },
    removeSpouse: (state) => {
      if (state.adultDetails.length > 1) {
        state.adultDetails = [state.adultDetails[0]];
      }
    },
    removeChild: (state, action: PayloadAction<number>) => {
      if (action.payload > -1) {
        if (state.childDetails[action.payload] === null) {
          const temp: PersonalDetail = {
            fullName: '',
            identityType: 0,
            identityNumber: '',
            dob: '',
            gender: '',
            ethnicity: '',
            email: '',
            mobileNumber: '',
            mailingAddress: {
              buildingName: '',
              areaName: '',
              cityName: undefined,
              postcode: '',
              state: '',
            },
          };
          state.childDetails[action.payload] = temp;
        }
        state.childDetails.splice(action.payload, 1);
      }
    },
    setBeneficiaryDetail: (state, action: PayloadAction<PersonalDetail>) => {
      state.beneficiaryDetail = {
        ...action.payload
      }
    },
    clearPersonalDetail: (state) => {
      if (state.adultDetails != undefined) {
        state.adultDetails = [];
      }
      if (state.childDetails != undefined) {
        state.childDetails = [];
      }
    },
    toggleAutoFlightClaim: (state, action: PayloadAction<{ value: boolean }>) => {
      // this action is used only by the proposer
      if (state.adultDetails.length) {
        state.adultDetails = state.adultDetails.map((adult) => ({
          ...adult,
          autoFlightClaim: action.payload.value,
        }))
      } else {
        state.adultDetails = Array(state.adultCount).fill({ autoFlightClaim: action.payload.value })
      }
    }
  },
});

export const {
  setAdultCount,
  setPersonalDetail,
  setInvalidKeys,
  removeInvalidKey,
  removeAllInvalidKeys,
  mapProposalDataToPersonalDetail,
  removeSpouse,
  removeChild,
  clearPersonalDetail,
  setBeneficiaryDetail,
  toggleAutoFlightClaim,
} = personalDetailSlice.actions;

export default personalDetailSlice.reducer;
